import React from 'react';
import styles from './HrSummaryCard.module.scss';
import { Typography } from '@navi/web-ui/lib/primitives';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import LocationIcon from 'src/assets/images/icons/LocationIcon';
import LanNumberIcon from 'src/assets/images/icons/LanNumberIcon';
import PersonIcon from 'src/assets/images/icons/PersonIcon';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import cx from 'classnames';
import Tooltip, { directions } from '../../components/Tooltip';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import CustomerImagePlaceholderIcon from '../../assets/images/icons/CustomerImagePlaceholderIcon';
import { noImageFoundText } from './dc-97/dc97Constant';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { HRC_CLICKSTREAM } from '../../service/clickStream.constant';
import MoneyBagIcon from 'src/assets/images/icons/MoneyBagIcon';
import { ExtensionHandler } from 'src/utils/extension.utils';
import {
  formatAmount,
  pluralisation,
  shortNumberNotationWithoutTrailingZeroesAndRounding
} from 'src/utils/commonUtils';
import InfoIcon from '@icons/InfoIcon/InfoIcon';
import VerticalSeperator from '@cp/src/components/VerticalSeperator';
import { DateFormat, getFormatDate } from '@cp/src/utils/DateHelper';

interface customerSummaryProps {
  customerName: string;
  genderAndAge: string;
  state: string;
  lanNumber: string;
  disbursedAmount: number;
  customerReferenceId: string;
  phoneNumber: string;
  totalTenureMonths: number;
  remainingAmount: number;
  remainingTenureMonths: number;
  lastPaidDpd: number;
  currentDpd: number;
  duesPaidLate: number;
  duesPaidOnTime: number;
  lastPaidDate: string;
}

export const CheckAndRender = (props: any) => {
  const { checkFunc, children, className } = props;
  return checkFunc() ? (
    <div className={cx(styles[`${className}`], styles.checkAndRenderContainer)}>{children}</div>
  ) : null;
};

const HrSummaryCard = (props: customerSummaryProps) => {
  const {
    customerName,
    genderAndAge,
    state,
    lanNumber,
    disbursedAmount,
    customerReferenceId,
    phoneNumber,
    totalTenureMonths,
    remainingAmount,
    remainingTenureMonths,
    lastPaidDpd,
    currentDpd,
    duesPaidLate,
    duesPaidOnTime,
    lastPaidDate
  } = props;
  const customerImageUrl = useSelector(
    (state: RootState) => state.common.humanReminderCustomerImage
  );
  const campaignId = useSelector((state: RootState) => state.common.userData?.campaignId);
  const reminderRefId = useSelector((state: RootState) => state.humanReminder.reminderRefId);

  const remainingAmountText =
    remainingAmount && remainingAmount > 0 ? formatAmount(remainingAmount) : 'NA';
  const disbursedAmountText =
    disbursedAmount && disbursedAmount > 0 ? formatAmount(disbursedAmount) : 'NA';
  const previousPaidDate = lastPaidDate ? getFormatDate(lastPaidDate, DateFormat.DD_MMM_YYYY) : '-';

  return (
    <GridContainer className={styles.summaryContainer}>
      <GridRow item className={cx(styles.m_0, styles.p_0)}>
        <GridColumn
          xs={2}
          className={cx(
            styles.m_0,
            customerImageUrl ? styles.customerImageContainer : styles.noImageFoundContainer,
            styles.imageColumdWidth
          )}
        >
          {customerImageUrl ? (
            <>
              <Tooltip
                content={
                  <div className={styles.imageContainer}>
                    <img className={styles.responsiveImage} src={customerImageUrl} />
                  </div>
                }
                wrapperClassName={styles.customerImageTooltip}
                onMouseEnterCallback={() => {
                  addClickstreamEvent(HRC_CLICKSTREAM.HRC_IMAGE_ZOOMED, {
                    lan: lanNumber,
                    customerReferenceId,
                    phoneNumber,
                    reminderRefId,
                    campaignId,
                    ameyoStatus: ExtensionHandler.currentAmeyoStatus()
                  });
                }}
              >
                <div className={styles.badgeImageContainer}>
                  <img className={styles.badgeResponsiveImage} src={customerImageUrl} />
                </div>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip
                content={noImageFoundText}
                direction={directions.BOTTOM}
                wrapperClassName={styles.noImageFoundTooltip}
              >
                <CustomerImagePlaceholderIcon />
              </Tooltip>
            </>
          )}
        </GridColumn>
        <GridColumn className={cx(styles.m_0, styles.customerNameContainer)}>
          <GridRow item xs={12} className={cx(styles.m_0, styles.p_0)}>
            <GridColumn className={cx(styles.p_0, styles.customerNameColumn)}>
              <div className={styles.customerNameContainer}>
                <Typography variant="p3" className={styles.customerName}>
                  {customerName}
                </Typography>
              </div>
            </GridColumn>
          </GridRow>
          <GridRow item xs={12} className={styles.customerInfo}>
            <CheckAndRender checkFunc={() => Boolean(genderAndAge)}>
              <PersonIcon />
              <Typography variant="p3" className={styles.value}>
                {genderAndAge}
              </Typography>
            </CheckAndRender>
            <CheckAndRender checkFunc={() => Boolean(state)}>
              <LocationIcon />
              <Typography variant="p3" className={styles.value}>
                {state}
              </Typography>
            </CheckAndRender>
            <CheckAndRender checkFunc={() => Boolean(lanNumber)}>
              <LanNumberIcon />
              <Typography variant="p3" className={styles.value}>{`LAN ${lanNumber}`}</Typography>
            </CheckAndRender>
            <CheckAndRender checkFunc={() => Boolean(disbursedAmount)}>
              <Tooltip
                direction={directions.TOP}
                content={
                  <>
                    <div className={styles.loanDetailsRecord}>
                      <span>Total Amount</span>
                      <span>{disbursedAmountText}</span>
                    </div>
                    <div className={styles.loanDetailsRecord}>
                      <span>Remaining Amount</span>
                      <span>{remainingAmountText}</span>
                    </div>
                  </>
                }
                wrapperClassName={styles.loanAmountTooltip}
              >
                <div className={styles.loanAmount}>
                  <MoneyBagIcon />
                  <Typography
                    variant="p3"
                    className={styles.value}
                  >{`Loan ${shortNumberNotationWithoutTrailingZeroesAndRounding(
                    disbursedAmount
                  )}`}</Typography>
                </div>
              </Tooltip>
            </CheckAndRender>
          </GridRow>
        </GridColumn>
        <div className={styles.bottomContainer}>
          {remainingTenureMonths !== totalTenureMonths ? (
            <>
              <Typography variant="p3" color="var(--grayscale-2)">
                Last Paid:
              </Typography>
              <Typography variant="h4" color="var(--grayscale-1)">
                DPD {lastPaidDpd} ({previousPaidDate})
              </Typography>
            </>
          ) : (
            <Typography variant="h4" color="var(--grayscale-1)">
              First EMI
            </Typography>
          )}
          <VerticalSeperator color="var(--grayscale-border-1)" height="16px" />
          <Typography variant="h4" color="var(--grayscale-1)">
            {totalTenureMonths} EMIs:
          </Typography>
          <Typography variant="p3" color="var(--grayscale-2)">
            Late <strong style={{ color: 'var(--grayscale-1)' }}>{duesPaidLate}</strong>
          </Typography>
          <VerticalSeperator color="var(--blue-border)" height="16px" />
          <Typography variant="p3" color="var(--grayscale-2)">
            On Time <strong style={{ color: 'var(--grayscale-1)' }}>{duesPaidOnTime}</strong>
          </Typography>
          <VerticalSeperator color="var(--blue-border)" height="16px" />
          <Typography variant="p3" color="var(--grayscale-2)">
            Remaining{' '}
            <strong style={{ color: 'var(--grayscale-1)' }}>{remainingTenureMonths}</strong>
          </Typography>
        </div>
      </GridRow>
    </GridContainer>
  );
};

export default HrSummaryCard;
